import {fromNullable, none, Option, some} from "fp-ts/lib/Option"
import StripeCheckout, {Token} from "react-stripe-checkout"
import {Button, DropdownItemProps, DropdownProps, Form, Loader, Select} from "semantic-ui-react"
import {
  BookerFragFragment,
  CardBrandEnum,
  CreateStripeCard as CREATE_STRIPE_CARD,
  StripeCard,
  StripeCardFragment,
  StripeCards,
  StripeConfig,
} from "../graphql/queries"
import {GraphQL} from "../graphql"
import {pollWithTimeout} from "../timeout"
import {ID} from "../model/id"
import * as React from "react"
import {errorUnionToEither, handleError, OrError} from "../graphql/errors"
import {flattenEither, liftOptionInEither, liftPromiseInEither, Loading} from "../tsutil"
import {WithModalStore} from "../App"
import {ModalStore} from "../actions"
import {right} from "fp-ts/lib/Either"
import {ErrorLabel} from "./AddBookingWizard"
import {bookingCardHasExpired} from "../model/booking"

export function cardBrandText(brand: CardBrandEnum): string {
  switch (brand) {
    case CardBrandEnum.Visa:
      return "Visa"
    case CardBrandEnum.American_Express:
      return "American Express"
    case CardBrandEnum.MasterCard:
      return "MasterCard"
    case CardBrandEnum.Discover:
      return "Discover"
    case CardBrandEnum.JCB:
      return "JCB"
    case CardBrandEnum.Diners_Club:
      return "Diners Club"
    case CardBrandEnum.Unknown:
      return "Unknown"
    case CardBrandEnum.UnionPay:
      return "UnionPay"
  }
}

type StripeCard = StripeCardFragment

function StripeCardInput(props: PropsWithPreviousCards) {
  const {
    stripeCard,
    payingBooker,
    onStripeCard,
    modalStore,
    previousCards,
    previousCardsLoading,
    refetchPreviousCards,
    disabled,
    additionalStripeCards,
    bookingDate,
  } = props
  if (!onStripeCard) {
    if (stripeCard.isSome()) {
      return <>{stripeCardText(payingBooker.id, stripeCard.value)}</>
    } else {
      return <>None</>
    }
  }

  // This map includes all the booker's previous stripe cards and the card that is already
  // selected (we can't assume the selected card is in the list of previous cards because the
  // previous cards might have been fetched before the selected card was added).
  function buildAllCardsIdMap(): Map<string, StripeCard> {
    const map = new Map<string, StripeCard>()
    for (const card of additionalStripeCards) {
      map.set(card.id, card)
    }
    for (const card of previousCards) {
      map.set(card.id, card)
    }
    stripeCard.map((card) => {
      map.set(card.id, card)
    })
    return map
  }

  const allCardsIdMap = buildAllCardsIdMap()

  function buildOptions(): Array<DropdownItemProps> {
    const array: Array<DropdownItemProps> = []
    array.push({
      value: noneValue,
      text: "None",
    })
    for (const card of allCardsIdMap.values()) {
      array.push({
        value: card.id,
        text: stripeCardText(payingBooker.id, card),
        // Other ways of setting content for the item:
        // label: "label",
        // description: "description",
        // content: "content",
        // children: "children",
      })
    }
    return array
  }

  const noneValue = "none" // empty string doesn't work. not sure why.
  const selectedValue = stripeCard.fold<string>(noneValue, (card) => card.id)

  const error = bookingCardHasExpired(stripeCard.toNullable(), bookingDate)
    ? "Your selected credit card expires before the date of this booking."
    : null

  return (
    <>
      <Form.Field>
        <Select
          search={false}
          onChange={(e: React.SyntheticEvent<HTMLElement>, field: DropdownProps) => {
            modalStore.handleActionThrow(async () => {
              const value = field.value as string
              const card: OrError<Option<StripeCard>> = right(
                value !== noneValue ? some(allCardsIdMap.get(value)!) : none,
              )
              ;(await onStripeCard(Promise.resolve(card)))()
            })
          }}
          selectOnBlur={false}
          selectOnNavigation={false}
          value={selectedValue}
          options={buildOptions()}
          upward
          width={10 /* 16 = 100% */}
          loading={previousCardsLoading}
          disabled={disabled}
        />
        <ErrorLabel error={error} />
      </Form.Field>
      <CUSoonStripeCheckout
        email={payingBooker.email}
        onToken={async (token) => {
          async function createCard(): Promise<OrError<Option<StripeCard>>> {
            const card = await createStripeCardWithModal(modalStore, token.id, payingBooker.id)
            // Once we've created our card, we should refetch the previous card list so that
            // it's included. We don't have to wait for the query to finish though, because the
            // refetch will show a spinner indicating the list is loading.
            modalStore.handlePromiseThrow(refetchPreviousCards())
            // Wrap in Option
            return card.map((x) => some(x))
          }
          return onStripeCard(createCard())
        }}
        label={"Add card"}
        modalStore={props.modalStore}
        disabled={disabled}
      >
        New card
      </CUSoonStripeCheckout>
    </>
  )
}

export const emSpace = "\u2003"

function stripeCardText(payingBookerId: string, card: StripeCard): string {
  if (
    card.brand === null ||
    card.last4 === null ||
    card.expiryYear === null ||
    card.expiryMonth === null
  ) {
    return "Card belonging to " + card.bookerName
  }

  // TODO It would be nice to show the card brand icon here
  let result =
    cardBrandText(card.brand) +
    emSpace +
    "•••• " +
    card.last4 +
    emSpace +
    card.expiryMonth +
    " / " +
    card.expiryYear

  if (card.bookerId !== payingBookerId) {
    result += emSpace + "(" + card.bookerName + ")"
  }

  return result
}

interface CUSoonStripeCheckoutProps extends WithModalStore {
  label?: string
  currency?: string
  email?: string
  /**
   * This callback is split in two stages.
   * 1. Do any processing you might want to do with the token.
   * 2. Modify any state based on the result of that processing.
   * The reason for this split is that this component needs to clear its "loading"
   * after 1., but before 2., since 2. might cause the component to be unmounted.
   */
  onToken: (token: Token) => Promise<() => void>
  amount?: number
  children: any
  rememberMe?: boolean
  disabled?: boolean
}

interface CUSoonStripeCheckoutPropsWithKey extends CUSoonStripeCheckoutProps {
  stripeKey: Loading<string>
}

class CUSoonStripeCheckoutRender extends React.Component<
  CUSoonStripeCheckoutPropsWithKey,
  {loading: boolean}
> {
  constructor(props: CUSoonStripeCheckoutPropsWithKey) {
    super(props)
    this.state = {loading: false}
  }

  render() {
    if (this.state.loading) {
      return (
        <Loader active={true} inline={true} size="small">
          Waiting for confirmation...
        </Loader>
      )
    }

    const {label, currency, email, stripeKey, amount, children, rememberMe = false} = this.props

    const loading = stripeKey.isNone()
    // TODO Why doesn't the disabled styling work on this button but it does on the rest of the form???
    const button = (
      <Button loading={loading} disabled={loading || this.props.disabled}>
        {children}
      </Button>
    )

    if (stripeKey.isSome() && !this.props.disabled) {
      // TODO
      // - Do we want to enable Alipay and Bitcoin?
      // - Technically the on click handler isn't on the button but instead a <span> that
      // surrounds it. Is that a problem? It seems to work.
      return (
        <StripeCheckout
          name={"C U Soon"}
          image={require("../images/cusoon-text.jpeg")}
          panelLabel={label}
          locale={"auto"}
          allowRememberMe={rememberMe}
          email={email}
          token={(token) =>
            this.props.modalStore.handleActionThrow(async () => {
              this.setState({loading: true})
              let fn
              try {
                fn = await this.props.onToken(token)
              } finally {
                this.setState({loading: false})
              }
              fn()
            })
          }
          label={label}
          currency={currency}
          stripeKey={stripeKey.value}
          amount={amount}
        >
          {button}
        </StripeCheckout>
      )
    } else {
      return button
    }
  }
}

interface PropsWithPreviousCards extends Props {
  previousCards: Array<StripeCard>
  refetchPreviousCards: () => Promise<void>
  previousCardsLoading: boolean
}

export function CUSoonStripeCheckout(props: CUSoonStripeCheckoutProps) {
  return (
    <GraphQL.Query
      query={StripeConfig}
      variables={{}}
      render={({data}) => {
        return (
          <CUSoonStripeCheckoutRender
            {...props}
            stripeKey={fromNullable(data ? data.stripeConfig.publicKey : null)}
          />
        )
      }}
    />
  )
}

function StripeCardRenderWithPreviousCards(props: Props) {
  const {payingBooker} = props
  return (
    <GraphQL.Query
      query={StripeCards}
      variables={{forBookerId: payingBooker.id}}
      render={({data, refetch, loading}) => {
        return handleError(data ? errorUnionToEither(data.stripeCards) : right(null), (data) => (
          <StripeCardInput
            {...props}
            previousCards={data ? data.stripeCards : []}
            previousCardsLoading={loading}
            refetchPreviousCards={async () => {
              await refetch()
            }}
          />
        ))
      }}
    />
  )
}

function createStripeCardWithModal(
  modalStore: ModalStore,
  token: string,
  payingBookerId: string,
): Promise<OrError<StripeCard>> {
  // Stripe has actually already verified the card before giving us the token. What we're
  // doing is saving the card against the booker's Stripe customer so we get back the card
  // last4 and brand and the card will be selected as the default card when the booker saves
  // the form.
  return modalStore.applyModal({
    error: (m) => ["Failed to save card", m],
    async action() {
      return createStripeCard(token, payingBookerId)
    },
  })
}

async function createStripeCard(
  token: string,
  payingBookerId: string,
): Promise<OrError<StripeCard>> {
  const data = await GraphQL.mutate(CREATE_STRIPE_CARD, {token, forBookerId: payingBookerId})
  const result = errorUnionToEither(data.createStripeCard).map((data) => waitStripeCard(data.uuid))
  return flattenEither(await liftPromiseInEither(result))
}

type OnStripeCard = (x: Promise<OrError<Option<StripeCard>>>) => Promise<() => void>

interface Props extends WithModalStore {
  payingBooker: BookerFragFragment
  stripeCard: Option<StripeCard>
  additionalStripeCards: Array<StripeCard>
  onStripeCard: OnStripeCard | null
  disabled: boolean
  bookingDate: string | null
}

function waitStripeCard(id: ID): Promise<OrError<StripeCard>> {
  return pollWithTimeout(async () => {
    const {stripeCard} = await GraphQL.query(StripeCard, {id}, {noCache: true})
    return liftOptionInEither(errorUnionToEither(stripeCard).map((x) => fromNullable(x.stripeCard)))
  })
}

export {StripeCardRenderWithPreviousCards as StripeCardInput}



// WEBPACK FOOTER //
// ./src/components/StripeCard.tsx